import {useCallback, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {useHistory} from 'react-router'
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Hook to manage the Check Balance drawer state with URL synchronization.
 * Opens drawer when ?checkBalance param is present, and updates URL when drawer state changes.
 *
 * @param {boolean} isGiftPage - Whether the current page is a gift card page
 * @returns {Object} - { isOpen, onOpen, onClose }
 */
const useCheckBalanceDrawer = (isGiftPage) => {
    const location = useLocation()
    const history = useHistory()
    const {
        isOpen: isCheckBalanceOpen,
        onOpen: checkBalanceOnOpen,
        onClose: checkBalanceOnClose
    } = useDisclosure()

    // Open check balance drawer if URL has ?checkBalance param
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.has('checkBalance') && isGiftPage && !isCheckBalanceOpen) {
            checkBalanceOnOpen()
        }
    }, [location.search, isGiftPage, isCheckBalanceOpen, checkBalanceOnOpen])

    // Wrapper to add ?checkBalance param when drawer opens
    const handleCheckBalanceOpen = useCallback(() => {
        const searchParams = new URLSearchParams(location.search)
        if (!searchParams.has('checkBalance')) {
            const currentParams = searchParams.toString()
            const newUrl = currentParams
                ? `${location.pathname}?${currentParams}&checkBalance`
                : `${location.pathname}?checkBalance`
            history.replace(newUrl)
        }
        checkBalanceOnOpen()
    }, [location.search, location.pathname, history, checkBalanceOnOpen])

    // Wrapper to remove ?checkBalance param when drawer closes
    const handleCheckBalanceClose = useCallback(() => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.has('checkBalance')) {
            searchParams.delete('checkBalance')
            const newUrl = searchParams.toString()
                ? `${location.pathname}?${searchParams.toString()}`
                : location.pathname
            history.replace(newUrl)
        }
        checkBalanceOnClose()
    }, [location.search, location.pathname, history, checkBalanceOnClose])

    return {
        isOpen: isCheckBalanceOpen,
        onOpen: handleCheckBalanceOpen,
        onClose: handleCheckBalanceClose
    }
}

export default useCheckBalanceDrawer
